import React, { useState, useEffect } from 'react';
import DashboardLayout from '../../components/DashboardLayout';
import api from '../../utils/api';

const PropertyOwnerDashboard = () => {
  const [properties, setProperties] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [showPropertyForm, setShowPropertyForm] = useState(false);
  const [propertyForm, setPropertyForm] = useState({
    title: '',
    description: '',
    address: '',
    city: '',
    country: '',
    price: '',
    bedrooms: '',
    bathrooms: '',
    features: [],
    furniture: [],
    inventory: [],
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [propertiesRes, analyticsRes] = await Promise.all([
        api.get('/api/properties'),
        api.get('/api/analytics/owner'),
      ]);
      setProperties(propertiesRes.data);
      setAnalytics(analyticsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const handleCreateProperty = async (e) => {
    e.preventDefault();
    const formData = new FormData();
    Object.keys(propertyForm).forEach(key => {
      if (key === 'features' || key === 'furniture' || key === 'inventory') {
        formData.append(key, JSON.stringify(propertyForm[key]));
      } else {
        formData.append(key, propertyForm[key]);
      }
    });

    try {
      await api.post('/api/properties', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setShowPropertyForm(false);
      setPropertyForm({
        title: '',
        description: '',
        address: '',
        city: '',
        country: '',
        price: '',
        bedrooms: '',
        bathrooms: '',
        features: [],
        furniture: [],
        inventory: [],
      });
      fetchData();
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to create property');
    }
  };

  const availableFeatures = [
    'Air Conditioner', 'Kettle', 'Wi-Fi', 'TV', 'Washing Machine',
    'Microwave', 'Refrigerator', 'Parking', 'Swimming Pool', 'Gym'
  ];

  const toggleFeature = (feature) => {
    const features = propertyForm.features.includes(feature)
      ? propertyForm.features.filter(f => f !== feature)
      : [...propertyForm.features, feature];
    setPropertyForm({ ...propertyForm, features });
  };

  return (
    <DashboardLayout title="Property Owner Dashboard">
      {analytics && (
        <div className="stats-grid">
          <div className="stat-card">
            <h3>Total Properties</h3>
            <div className="value">{analytics.totalProperties}</div>
          </div>
          <div className="stat-card">
            <h3>Total Revenue</h3>
            <div className="value">${analytics.totalRevenue?.toLocaleString() || 0}</div>
          </div>
          <div className="stat-card">
            <h3>Net Revenue</h3>
            <div className="value">${analytics.totalNetRevenue?.toLocaleString() || 0}</div>
          </div>
          <div className="stat-card">
            <h3>Platform Fee</h3>
            <div className="value">{analytics.platformFeePercentage?.toFixed(1) || 0}%</div>
          </div>
        </div>
      )}

      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2>My Properties</h2>
          <button onClick={() => setShowPropertyForm(!showPropertyForm)} className="btn btn-primary">
            {showPropertyForm ? 'Cancel' : 'Add Property'}
          </button>
        </div>

        {showPropertyForm && (
          <form onSubmit={handleCreateProperty} style={{ marginBottom: '20px', padding: '20px', background: '#f8f9fa', borderRadius: '4px' }}>
            <div className="form-group">
              <label>Title</label>
              <input
                type="text"
                value={propertyForm.title}
                onChange={(e) => setPropertyForm({ ...propertyForm, title: e.target.value })}
                required
              />
            </div>
            <div className="form-group">
              <label>Description</label>
              <textarea
                value={propertyForm.description}
                onChange={(e) => setPropertyForm({ ...propertyForm, description: e.target.value })}
                required
              />
            </div>
            <div className="form-group">
              <label>Address</label>
              <input
                type="text"
                value={propertyForm.address}
                onChange={(e) => setPropertyForm({ ...propertyForm, address: e.target.value })}
                required
              />
            </div>
            <div className="form-group">
              <label>City</label>
              <input
                type="text"
                value={propertyForm.city}
                onChange={(e) => setPropertyForm({ ...propertyForm, city: e.target.value })}
                required
              />
            </div>
            <div className="form-group">
              <label>Country</label>
              <input
                type="text"
                value={propertyForm.country}
                onChange={(e) => setPropertyForm({ ...propertyForm, country: e.target.value })}
                required
              />
            </div>
            <div className="form-group">
              <label>Price per Night ($)</label>
              <input
                type="number"
                value={propertyForm.price}
                onChange={(e) => setPropertyForm({ ...propertyForm, price: e.target.value })}
                required
              />
            </div>
            <div className="form-group">
              <label>Bedrooms</label>
              <input
                type="number"
                value={propertyForm.bedrooms}
                onChange={(e) => setPropertyForm({ ...propertyForm, bedrooms: e.target.value })}
                required
              />
            </div>
            <div className="form-group">
              <label>Bathrooms</label>
              <input
                type="number"
                value={propertyForm.bathrooms}
                onChange={(e) => setPropertyForm({ ...propertyForm, bathrooms: e.target.value })}
                required
              />
            </div>
            <div className="form-group">
              <label>Features</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                {availableFeatures.map(feature => (
                  <label key={feature} style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <input
                      type="checkbox"
                      checked={propertyForm.features.includes(feature)}
                      onChange={() => toggleFeature(feature)}
                    />
                    {feature}
                  </label>
                ))}
              </div>
            </div>
            <button type="submit" className="btn btn-success">Submit Property</button>
          </form>
        )}

        <table className="table">
          <thead>
            <tr>
              <th>Title</th>
              <th>Address</th>
              <th>Status</th>
              <th>Availability</th>
              <th>Price</th>
            </tr>
          </thead>
          <tbody>
            {properties.map((property) => (
              <tr key={property.id}>
                <td>{property.title}</td>
                <td>{property.address}</td>
                <td>
                  <span className={`badge badge-${property.status === 'approved' ? 'success' : property.status === 'rejected' ? 'danger' : 'warning'}`}>
                    {property.status}
                  </span>
                </td>
                <td>
                  <span className={`badge badge-${property.availabilityStatus === 'available' ? 'success' : 'secondary'}`}>
                    {property.availabilityStatus}
                  </span>
                </td>
                <td>${property.price}/night</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {analytics && analytics.propertyPerformance && (
        <div className="card">
          <h2>Property Performance</h2>
          <table className="table">
            <thead>
              <tr>
                <th>Property</th>
                <th>Total Bookings</th>
                <th>Revenue</th>
                <th>Platform Fees</th>
                <th>Net Revenue</th>
              </tr>
            </thead>
            <tbody>
              {analytics.propertyPerformance.map((perf) => (
                <tr key={perf.propertyId}>
                  <td>{perf.title}</td>
                  <td>{perf.totalBookings}</td>
                  <td>${perf.revenue?.toLocaleString() || 0}</td>
                  <td>${perf.platformFees?.toLocaleString() || 0}</td>
                  <td>${perf.netRevenue?.toLocaleString() || 0}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </DashboardLayout>
  );
};

export default PropertyOwnerDashboard;

